import { CornerDownLeft, Link2, Search, SunMoon, X, type LucideIcon } from "lucide-react";
import { Fragment, useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from "react";
import { useTranslation } from "react-i18next";
import { applyThemePreference } from "../lib/theme.js";
import { fuzzyMatches, matchPages, pageIndex } from "../features/search/pageSearch.js";
import { type Tab } from "../features/shell/navigation.js";

// Keyboard-driven command palette (#501): Cmd/Ctrl-K opens an accessible
// overlay that fuzzy-filters the app's navigation tabs plus a few quick
// actions. Navigation reuses App's existing tab-switch callback (navigateToTab)
// rather than re-implementing routing; the quick actions ("Toggle theme",
// "Copy link to this page") are self-contained so this doesn't depend on the
// separate Share work (#496).
//
// Open state is CONTROLLED by App (#1055) so the header's search button can
// open it too — the palette is mounted as a sibling of Shell, so context living
// inside Shell can't reach it and the state has to sit above both. Ctrl/Cmd-K
// keeps its own listener here and its toggle semantics are unchanged.
//
// #1037 settled the palette's fate: it STAYS, as the keyboard fast path, but it
// no longer owns a navigation index. The page list and the matcher moved to
// features/search/pageSearch.ts and the Search page now reads the same two, so
// "search the app's own pages" has one implementation with two front-ends
// rather than two implementations that can drift apart. What is still only
// here are the quick actions: they are commands, not household records, and
// site search deliberately does not list them.

// Stable ids, not copy: the renderer compares an item's group with the previous
// item's to decide where a heading goes, so this must never hold translated
// text. The heading itself is resolved from PALETTE_GROUP_KEYS at render.
type PaletteGroup = "navigate" | "actions";

const PALETTE_GROUP_KEYS: Record<PaletteGroup, string> = {
  navigate: "palette.groups.navigate",
  actions: "palette.groups.actions"
};

type PaletteItem = {
  id: string;
  group: PaletteGroup;
  label: string;
  hint?: string;
  /** Extra text the matcher searches. Pages carry their own; actions need it. */
  keywords?: string;
  icon: LucideIcon;
  perform: () => void;
};

function copyToClipboardFallback(text: string): boolean {
  try {
    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.setAttribute("readonly", "");
    textarea.style.position = "absolute";
    textarea.style.left = "-9999px";
    document.body.appendChild(textarea);
    textarea.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(textarea);
    return ok;
  } catch {
    return false;
  }
}

export type CommandPaletteProps = {
  /** App's existing tab-switch mechanism (App.navigateToTab). */
  onSelectTab: (tab: Tab) => void;
  /** The currently active tab, used to mark the current page. */
  activeTab?: Tab;
  /** Controlled visibility — App owns the state (#1055). */
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * Demo mode hides a couple of tabs from the sidebar. The palette has to hide
   * the same ones: this PR gives it a visible header button, which turns it
   * from a keyboard shortcut into a door a demo visitor can actually find, and
   * therefore into the way around that restriction.
   */
  demoMode?: boolean;
};

export function CommandPalette({ onSelectTab, activeTab, open, onOpenChange, demoMode = false }: CommandPaletteProps) {
  const { t } = useTranslation("common");
  // The page names come from the SHELL bundle, the same table the sidebar
  // resolves: listing them from `tabs[].label` (the English source) put
  // "Finance" in the palette while the nav beside it said "Finanzas".
  const { t: tShell } = useTranslation("shell");
  const [query, setQuery] = useState("");
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [status, setStatus] = useState("");

  const inputRef = useRef<HTMLInputElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);
  // Read inside the global keydown handler so the toggle never closes over a
  // stale `open` value or a stale callback (the listener is registered once).
  // Both are props now (#1055), so the hazard the original comment warned about
  // applies to the setter as much as to the flag.
  const openRef = useRef(open);
  const onOpenChangeRef = useRef(onOpenChange);

  const baseId = useId();
  const listboxId = `${baseId}-listbox`;
  const optionId = (index: number) => `${baseId}-option-${index}`;

  // LAYOUT-timed. These refs exist so the global Cmd/Ctrl-K handler reads the
  // current controlled state, and a passive effect fires after the browser can
  // already deliver the next keystroke: press the shortcut immediately after
  // opening from the header button (or closing with Escape) and the handler
  // would read the previous value and send it straight back, so the shortcut
  // appeared to do nothing.
  useLayoutEffect(() => {
    openRef.current = open;
  }, [open]);

  useLayoutEffect(() => {
    onOpenChangeRef.current = onOpenChange;
  }, [onOpenChange]);

  const close = useCallback(() => onOpenChange(false), [onOpenChange]);

  const toggleTheme = useCallback(() => {
    const current = document.documentElement.dataset.theme === "dark" ? "dark" : "light";
    applyThemePreference(current === "dark" ? "light" : "dark");
  }, []);

  const copyLink = useCallback(() => {
    const url = window.location.href;
    const report = (ok: boolean) => setStatus(ok ? t("palette.copied") : t("palette.copyFailed"));
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(url)
        .then(() => report(true))
        .catch(() => report(copyToClipboardFallback(url)));
      return;
    }
    report(copyToClipboardFallback(url));
  }, [t]);

  // The shared page index (#1037). Demo mode and the English-source fallback
  // both live in pageIndex/matchPages now, so this file no longer decides
  // either.
  const pageEntries = useMemo(() => pageIndex({ translate: tShell, demoMode }), [tShell, demoMode]);

  const actionItems = useMemo<PaletteItem[]>(() => {
    return [
      {
        id: "action-toggle-theme",
        group: "actions",
        label: t("palette.toggleTheme"),
        hint: t("palette.toggleThemeHint"),
        keywords: "toggle theme dark light appearance mode color",
        icon: SunMoon,
        perform: () => {
          toggleTheme();
          close();
        }
      },
      {
        id: "action-copy-link",
        group: "actions",
        label: t("palette.copyLink"),
        hint: t("palette.copyLinkHint"),
        keywords: "copy link share url address clipboard page",
        icon: Link2,
        // Stays open so the copied-status feedback is visible/announced.
        perform: copyLink
      }
    ];
  }, [close, toggleTheme, copyLink, t]);

  const filtered = useMemo<PaletteItem[]>(() => {
    const trimmed = query.trim();
    // No MAX_PAGE_RESULTS here. Site search caps the page group because the rest
    // of that screen belongs to household data; the palette IS the page list, so
    // capping it would hide pages it used to show.
    const pages = trimmed ? matchPages(trimmed, pageEntries, pageEntries.length) : pageEntries;
    const navItems: PaletteItem[] = pages.map((page) => ({
      id: `nav-${page.tab}`,
      group: "navigate",
      label: page.label,
      hint: activeTab === page.tab ? t("palette.current") : undefined,
      icon: page.icon,
      perform: () => {
        onSelectTab(page.tab);
        close();
      }
    }));
    const actions = actionItems.filter((item) => fuzzyMatches(trimmed, `${item.label} ${item.keywords ?? ""}`));
    return [...navItems, ...actions];
  }, [pageEntries, query, activeTab, onSelectTab, close, t, actionItems]);

  // Register the global Cmd/Ctrl-K shortcut once. It preventDefaults so the
  // browser's own Ctrl-K (some focus the address bar) doesn't fire, and toggles
  // the palette. Typing inside the palette's own input is unaffected because the
  // input handles plain keystrokes; only the modifier combo is intercepted.
  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if ((event.metaKey || event.ctrlKey) && !event.altKey && (event.key === "k" || event.key === "K")) {
        event.preventDefault();
        onOpenChangeRef.current(!openRef.current);
      }
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  // On open: remember the trigger, reset the query/highlight/status, and move
  // focus into the input. The capture has to happen HERE rather than at each
  // call site (#1055) — the palette can now be opened by Ctrl-K or by the
  // header's search button, and only one of those runs code we own. Reading
  // `document.activeElement` at the top of this effect is correct for both:
  // focus has not moved yet at this point, since the input is focused by the
  // setTimeout below.
  useEffect(() => {
    if (!open) return undefined;
    previouslyFocused.current = document.activeElement as HTMLElement | null;
    setQuery("");
    setHighlightedIndex(0);
    setStatus("");
    const handle = window.setTimeout(() => inputRef.current?.focus(), 0);
    return () => window.clearTimeout(handle);
  }, [open]);

  // On close: restore focus to whatever was focused before opening.
  //
  // The remembered element has often left the document by now — selecting a
  // Navigate item unmounts the page that owned it — and focusing a detached
  // node silently drops focus to <body>. Skipping the call avoids targeting a
  // detached node but strands the keyboard user just the same, which is the
  // common path rather than an edge case. So there is a fallback: the header's
  // search button, which opened the palette, is on every page and survives
  // navigation.
  useEffect(() => {
    if (open) return;
    const previous = previouslyFocused.current;
    previouslyFocused.current = null;
    // Nothing remembered means the palette was never opened — this effect also
    // runs on the initial closed mount, and taking the fallback there would
    // move focus to the header search button on every page load.
    if (!previous) return;
    if (typeof previous.focus === "function" && previous.isConnected) {
      previous.focus();
      return;
    }
    const fallback = document.querySelector<HTMLElement>("[data-palette-return-focus]");
    fallback?.focus();
  }, [open]);

  // Reset the highlight to the top result on each keystroke so the best match
  // is pre-selected (also keeps the index in range as the list shrinks).
  useEffect(() => {
    setHighlightedIndex(0);
  }, [query]);

  // Scroll the active option into view as the highlight moves by keyboard.
  useEffect(() => {
    if (!open) return;
    document.getElementById(optionId(highlightedIndex))?.scrollIntoView({ block: "nearest" });
  }, [highlightedIndex, open]);

  const runItem = useCallback((item: PaletteItem) => {
    item.perform();
  }, []);

  function moveHighlight(delta: number) {
    if (filtered.length === 0) return;
    setHighlightedIndex((current) => (current + delta + filtered.length) % filtered.length);
  }

  function focusableInPanel(): HTMLElement[] {
    if (!panelRef.current) return [];
    return Array.from(
      panelRef.current.querySelectorAll<HTMLElement>("input, button:not([disabled])")
    );
  }

  function onPanelKeyDown(event: ReactKeyboardEvent<HTMLDivElement>) {
    if (event.key === "Escape") {
      event.preventDefault();
      close();
      return;
    }
    if (event.key === "ArrowDown") {
      event.preventDefault();
      moveHighlight(1);
      return;
    }
    if (event.key === "ArrowUp") {
      event.preventDefault();
      moveHighlight(-1);
      return;
    }
    if (event.key === "Home") {
      event.preventDefault();
      setHighlightedIndex(0);
      return;
    }
    if (event.key === "End") {
      event.preventDefault();
      setHighlightedIndex(Math.max(0, filtered.length - 1));
      return;
    }
    if (event.key === "Enter") {
      event.preventDefault();
      const item = filtered[highlightedIndex];
      if (item) runItem(item);
      return;
    }
    if (event.key === "Tab") {
      // Trap focus within the dialog (only the input and buttons are focusable;
      // options are managed via aria-activedescendant, not the tab order).
      const focusable = focusableInPanel();
      if (focusable.length === 0) {
        event.preventDefault();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!first || !last) return;
      const active = document.activeElement;
      if (event.shiftKey && active === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    }
  }

  if (!open) return null;

  return (
    <div
      className="command-palette-overlay"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) close();
      }}
    >
      <div
        className="command-palette-panel"
        role="dialog"
        aria-modal="true"
        aria-label={t("palette.dialog")}
        ref={panelRef}
        onKeyDown={onPanelKeyDown}
      >
        <div className="command-palette-search">
          <Search size={18} aria-hidden="true" />
          <input
            ref={inputRef}
            type="text"
            role="combobox"
            aria-expanded="true"
            aria-controls={listboxId}
            aria-activedescendant={filtered.length ? optionId(highlightedIndex) : undefined}
            aria-label={t("palette.searchLabel")}
            placeholder={t("palette.searchPlaceholder")}
            autoComplete="off"
            spellCheck={false}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
          <button
            type="button"
            className="command-palette-close icon-button"
            aria-label={t("palette.close")}
            title={t("actions.close")}
            onClick={close}
          >
            <X size={16} />
          </button>
        </div>

        {filtered.length === 0 ? (
          <p className="command-palette-empty">{t("palette.noMatches", { query: query.trim() })}</p>
        ) : (
          <ul className="command-palette-list" role="listbox" id={listboxId} aria-label={t("palette.listLabel")}>
            {filtered.map((item, index) => {
              const showGroup = index === 0 || filtered[index - 1]?.group !== item.group;
              const Icon = item.icon;
              const active = index === highlightedIndex;
              return (
                <Fragment key={item.id}>
                  {showGroup ? (
                    <li className="command-palette-group-label" role="presentation" aria-hidden="true">
                      {t(PALETTE_GROUP_KEYS[item.group])}
                    </li>
                  ) : null}
                  <li
                    id={optionId(index)}
                    role="option"
                    aria-selected={active}
                    className={active ? "command-palette-option active" : "command-palette-option"}
                    onMouseEnter={() => setHighlightedIndex(index)}
                    // Keep focus on the input (so the copied-link status stays
                    // announced) instead of letting the click blur it.
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => runItem(item)}
                  >
                    <Icon size={17} aria-hidden="true" />
                    <span className="command-palette-option-label">{item.label}</span>
                    {item.hint ? <span className="command-palette-option-hint">{item.hint}</span> : null}
                  </li>
                </Fragment>
              );
            })}
          </ul>
        )}

        <div className="command-palette-footer">
          <span className="command-palette-hints">
            <kbd>↑</kbd>
            <kbd>↓</kbd>
            <span>{t("palette.hints.navigate")}</span>
            <kbd><CornerDownLeft size={11} /></kbd>
            <span>{t("palette.hints.select")}</span>
            <kbd>esc</kbd>
            <span>{t("palette.hints.close")}</span>
          </span>
          <span className="command-palette-status" role="status" aria-live="polite">{status}</span>
        </div>
      </div>
    </div>
  );
}
